import React, { useState, useEffect, useCallback } from 'react';
import lpApi from '../../../helpers/lpApi';

// ============================================================================
// Big Projects · Payment Methods — admin settings for Large Projects ON-SESSION
// payments (the client paying NOW: the award deposit / "pay this milestone now").
//
// DEDICATED to Large Projects and SEPARATE from the small-jobs payment settings —
// nothing here touches the small-jobs money-path.
//
// Card is always ON (escrow needs a card path + the off-session held-card charge
// is card-only). Affirm / Klarna (BNPL) are opt-in and apply to ON-SESSION
// payments only — off-session held-card charges stay card regardless.
//
// Stored on besthand-api as SiteSettings.largeProjectsPaymentMethods, a JSON map
// e.g. {"card":true,"affirm":false,"klarna":false}. All ops run through the
// `/lp-graphql` proxy via `lpApi` (no LP resolver is duplicated in besthand-web).
// besthand-api's requireAdmin gates the updateLpPaymentMethods mutation.
// ============================================================================

const Q_METHODS = `
  query {
    lpPaymentMethods {
      status errorMessage lpPaymentMethodsJson
    }
  }
`;

const M_UPDATE = `
  mutation ($methodsJson: String) {
    updateLpPaymentMethods(methodsJson: $methodsJson) {
      status errorMessage lpPaymentMethodsJson
    }
  }
`;

// Toggleable methods (card is always on and not toggleable here).
const BNPL_METHODS = [
  {
    key: 'affirm',
    label: 'Affirm',
    blurb: 'Pay-over-time at checkout. US deposits/milestones from $50 to $30,000.',
  },
  {
    key: 'klarna',
    label: 'Klarna',
    blurb: 'Pay-over-time / pay-in-4 at checkout. US payments from $1 to $10,000.',
  },
  {
    key: 'afterpay_clearpay',
    label: 'Afterpay / Clearpay',
    blurb: 'Pay-in-4 at checkout. US single-order payments from $1 to $4,000.',
  },
  {
    key: 'cashapp',
    label: 'Cash App Pay',
    blurb: 'Pay instantly from a Cash App balance. US payments from $1 to $30,000.',
  },
];

// Non-card toggle keys (drives parse + save so adding a method needs one edit).
const TOGGLE_KEYS = BNPL_METHODS.map((m) => m.key);

function emptyMethods() {
  const out = { card: true };
  TOGGLE_KEYS.forEach((k) => { out[k] = false; });
  return out;
}

function parseMethods(json) {
  const out = emptyMethods();
  if (!json) return out;
  let parsed;
  try {
    parsed = JSON.parse(json);
  } catch (e) {
    return out;
  }
  if (!parsed || typeof parsed !== 'object') return out;
  TOGGLE_KEYS.forEach((m) => {
    if (typeof parsed[m] === 'boolean') out[m] = parsed[m];
  });
  out.card = true; // always on
  return out;
}

// --- styles (inline, mirrors EscrowRules.js / lightweight admin pages) ---
const S = {
  page: { maxWidth: 720, margin: '0 auto', padding: 24, fontFamily: "'Inter', -apple-system, sans-serif", color: '#0f1214' },
  h2: { margin: '0 0 4px', fontSize: 24, fontWeight: 800 },
  hint: { color: '#6b7280', fontSize: 14, marginBottom: 20, lineHeight: 1.5 },
  card: { border: '1px solid #e7e9ec', borderRadius: 14, padding: '22px 24px', marginBottom: 16, boxShadow: '0 6px 18px rgba(15,18,20,0.03)' },
  cardTitle: { fontSize: 16, fontWeight: 800, margin: '0 0 4px' },
  cardSub: { color: '#6b7280', fontSize: 13, marginTop: 0, marginBottom: 16, lineHeight: 1.5 },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid #f3f5f6' },
  rowLast: { borderBottom: 'none' },
  methodName: { fontSize: 15, fontWeight: 800, color: '#0f1214' },
  methodBlurb: { fontSize: 13, color: '#6b7280', marginTop: 2, maxWidth: 460, lineHeight: 1.45 },
  lockedPill: { fontSize: 11, fontWeight: 800, color: '#065f46', background: '#ecfdf5', borderRadius: 999, padding: '4px 10px', letterSpacing: 0.3 },
  // toggle switch
  toggle: (on) => ({ width: 46, height: 27, borderRadius: 999, background: on ? '#e8232b' : '#d1d5db', position: 'relative', cursor: 'pointer', transition: 'background .15s', flex: '0 0 auto' }),
  knob: (on) => ({ position: 'absolute', top: 3, left: on ? 22 : 3, width: 21, height: 21, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.25)', transition: 'left .15s' }),
  actions: { display: 'flex', gap: 10, marginTop: 22 },
  btn: { background: '#e8232b', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 22px', fontSize: 14, fontWeight: 800, cursor: 'pointer' },
  btnDisabled: { background: '#f0bcbe', cursor: 'not-allowed' },
  alert: (ok) => ({ padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 16, background: ok ? '#ecfdf5' : '#fef2f2', color: ok ? '#065f46' : '#991b1b' }),
  note: { fontSize: 12.5, color: '#9ca3af', marginTop: 14, lineHeight: 1.5 },
  empty: { color: '#6b7280', fontSize: 14, padding: '40px 0', textAlign: 'center' },
};

function LpPaymentMethods() {
  const [methods, setMethods] = useState(emptyMethods());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const firstError = (res) => (res && res.errors && res.errors.length ? res.errors[0].message : 'Something went wrong.');

  const load = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    try {
      const res = await lpApi(Q_METHODS);
      if (res.errors) {
        setMsg({ error: firstError(res) });
      } else {
        const env = res.data && res.data.lpPaymentMethods;
        if (env && env.status === 200) {
          setMethods(parseMethods(env.lpPaymentMethodsJson));
        } else if (env) {
          setMsg({ error: env.errorMessage || `Error ${env.status}` });
        }
      }
    } catch (e) {
      setMsg({ error: `Could not load payment methods: ${e.message}` });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (key) => {
    setMethods((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const save = async () => {
    setMsg(null);
    const payload = { card: true };
    TOGGLE_KEYS.forEach((k) => { payload[k] = !!methods[k]; });
    const methodsJson = JSON.stringify(payload);
    setSaving(true);
    try {
      const res = await lpApi(M_UPDATE, { methodsJson });
      if (res.errors) { setMsg({ error: firstError(res) }); setSaving(false); return; }
      const env = res.data && res.data.updateLpPaymentMethods;
      if (env && env.status === 200) {
        setMethods(parseMethods(env.lpPaymentMethodsJson));
        setMsg({ ok: 'Payment methods saved.' });
      } else {
        setMsg({ error: (env && env.errorMessage) || `Error ${env && env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Save failed: ${e.message}` });
    }
    setSaving(false);
  };

  return (
    <div style={S.page}>
      <h2 style={S.h2}>Payment Methods</h2>
      <div style={S.hint}>
        Large Projects — which methods a client can use when they <strong>pay now</strong> (the
        award deposit and “pay this milestone” steps). Buy-now-pay-later options apply to these
        on-session payments only; the held card used for later automatic milestone charges is
        always card. These settings are separate from the standard small-jobs checkout.
      </div>

      {msg && <div style={S.alert(!!msg.ok)}>{msg.ok || msg.error}</div>}

      {loading ? (
        <div style={S.empty}>Loading…</div>
      ) : (
        <React.Fragment>
          <div style={S.card}>
            <p style={S.cardTitle}>Card</p>
            <p style={S.cardSub}>Always available — escrow requires a card path.</p>
            <div style={{ ...S.row, ...S.rowLast }}>
              <div>
                <div style={S.methodName}>Credit / debit card</div>
                <div style={S.methodBlurb}>Standard card payment and the held card for automatic milestone charges.</div>
              </div>
              <span style={S.lockedPill}>ALWAYS ON</span>
            </div>
          </div>

          <div style={S.card}>
            <p style={S.cardTitle}>Buy now, pay later</p>
            <p style={S.cardSub}>
              Let clients finance a deposit or milestone at checkout. The platform is still paid
              in full up front by the provider (Stripe). Enable per method.
            </p>
            {BNPL_METHODS.map((m, i) => (
              <div key={m.key} style={i === BNPL_METHODS.length - 1 ? { ...S.row, ...S.rowLast } : S.row}>
                <div>
                  <div style={S.methodName}>{m.label}</div>
                  <div style={S.methodBlurb}>{m.blurb}</div>
                </div>
                <div
                  role="switch"
                  aria-checked={!!methods[m.key]}
                  aria-label={`Enable ${m.label}`}
                  tabIndex={0}
                  onClick={() => toggle(m.key)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(m.key); } }}
                  style={S.toggle(!!methods[m.key])}
                >
                  <span style={S.knob(!!methods[m.key])} />
                </div>
              </div>
            ))}
            <div style={S.note}>
              A method only appears at checkout when the charge amount is inside its provider
              limits (Affirm $50–$30,000, Klarna $1–$10,000). Card always appears.
            </div>
          </div>

          <div style={S.actions}>
            <button
              type="button"
              style={saving ? { ...S.btn, ...S.btnDisabled } : S.btn}
              onClick={save}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </React.Fragment>
      )}
    </div>
  );
}

export default LpPaymentMethods;
